import type { ChatStreamEvent } from '@apaxir/shared';
import { config } from '../config';

export interface SendMessageParams {
  botId: string;
  visitorId: string;
  conversationId?: string;
  message: string;
  signal?: AbortSignal;
  /** Phase 3f.3 — Turnstile token, supplied only on the first message
   *  of a new conversation when the bot has Turnstile enabled. */
  cfTurnstileToken?: string;
}

/**
 * Stream chat from Worker. Mirrors server's SSE parsing.
 *
 * Yields ChatStreamEvent objects. Caller handles:
 *   - 'token'  append to bubble
 *   - 'done'   capture conversationId, finalize
 *   - 'error'  show error (component maps `code` → localized string)
 *
 * Errors are emitted with a structured `code` (`rate_limited` | `generic`) plus
 * a raw `message` fallback; the widget component picks the localized string.
 * AbortSignal cancels the fetch — used when user closes the widget mid-stream.
 */
export async function* sendMessage(
  params: SendMessageParams,
): AsyncGenerator<ChatStreamEvent, void, unknown> {
  const body = {
    botId: params.botId,
    visitorId: params.visitorId,
    conversationId: params.conversationId,
    message: params.message,
    // Server treats undefined as "no token sent" — only attach the key
    // when we actually have a token so the JSON payload stays clean.
    ...(params.cfTurnstileToken ? { cfTurnstileToken: params.cfTurnstileToken } : {}),
  };

  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: params.signal,
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') return;
    yield { type: 'error', code: 'generic', message: 'network_error' };
    return;
  }

  if (!res.ok) {
    let code: 'rate_limited' | 'generic' = 'generic';
    let retryAfterSec: number | undefined;
    let rawMessage = `http_${res.status}`;
    try {
      const data = (await res.json()) as {
        error?: string;
        code?: 'rate_limited' | 'generic';
        retryAfterSec?: number;
      };
      if (data.code === 'rate_limited' || data.error === 'rate_limited') {
        code = 'rate_limited';
      }
      if (typeof data.retryAfterSec === 'number') retryAfterSec = data.retryAfterSec;
      if (data.error) rawMessage = data.error;
    } catch {
      // non-JSON body — keep defaults
    }
    yield {
      type: 'error',
      code,
      message: rawMessage,
      ...(retryAfterSec !== undefined ? { retryAfterSec } : {}),
    };
    return;
  }

  if (!res.body) {
    yield { type: 'error', code: 'generic', message: 'empty_stream' };
    return;
  }

  // Parse SSE stream
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const evt of events) {
        const dataLine = evt.split('\n').find((l) => l.startsWith('data: '));
        if (!dataLine) continue;
        const json = dataLine.slice(6).trim();
        if (!json) continue;
        try {
          yield JSON.parse(json) as ChatStreamEvent;
        } catch {
          // ignore malformed frames
        }
      }
    }
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') return;
    yield { type: 'error', code: 'generic', message: 'stream_interrupted' };
  }
}
